import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ArrowRight, BookOpenCheck, ExternalLink, Scale } from "lucide-react";
import { api, ApiError } from "../../api/client";
import { card, pageHeading, pageSubtitle, theme, type as text } from "../../theme";
import { libraryPath, playbookPath, usePlaybooks } from "../../lib/playbooks";
import { ShellLink } from "../../components/ShellLink";

// ── Playbooks ───────────────────────────────────────────────────────────────
// Good practice for the things the platform helps with, in the publishers' own words.
// Every passage under a step is quoted verbatim from a document in the Library and cited;
// the step titles are only labels. The criteria at the end are how the compliance check
// applies the playbook to this organisation — the platform's logic, shown so a verdict
// that cites "IR-3" can be followed, and never presented as the source's words.

interface Quote { doc: string; ref?: string; text: string }
interface Step { title: string; to?: string; quotes: Quote[] }
interface Phase { title: string; steps: Step[] }
interface Option { id: string; question: string; positions: Quote[] }
interface Criterion { id: string; practice: string; evidence: string; gapWhen: string }
interface SourceDoc { id: string; title: string; publisher: string; edition: string; licenceLabel: string; sourceUrl: string; readable: boolean }
interface PlaybookDetail {
  id: string; code: string; title: string; summary: string; when: string;
  boundaries: string | null; options: Option[]; phases: Phase[]; criteria: Criterion[];
  related: Array<{ id: string; title: string }>;
}

const linkStyle = { color: theme.color.accentText, textDecoration: "none", fontWeight: 600 } as const;

function Citation({ quote, docs }: { quote: Quote; docs: Map<string, SourceDoc> }) {
  const doc = docs.get(quote.doc);
  const label = doc ? `${doc.publisher} — ${doc.title}` : quote.doc;
  return (
    <span style={{ ...text.hint, display: "block", marginTop: 4 }}>
      {doc?.readable
        ? <ShellLink to={libraryPath(doc.id)} style={linkStyle}>{label}</ShellLink>
        : <a href={doc?.sourceUrl} target="_blank" rel="noopener noreferrer" style={linkStyle}>{label} <ExternalLink size={11} /></a>}
      {quote.ref ? `, ${quote.ref}` : ""}
    </span>
  );
}

function QuoteBlock({ quote, docs }: { quote: Quote; docs: Map<string, SourceDoc> }) {
  return (
    <figure style={{ margin: "8px 0 0", padding: "8px 12px", borderLeft: `3px solid ${theme.color.accent}`, background: "var(--c-panelAlt)", borderRadius: 3 }}>
      <blockquote style={{ margin: 0, fontSize: 13, lineHeight: 1.6, color: theme.color.text }}>“{quote.text}”</blockquote>
      <figcaption><Citation quote={quote} docs={docs} /></figcaption>
    </figure>
  );
}

function PlaybookList() {
  const list = usePlaybooks();
  return (
    <div style={{ padding: "20px 24px" }}>
      <h1 style={pageHeading}>Playbooks</h1>
      <p style={{ ...pageSubtitle, margin: "0 0 20px" }}>
        Good practice for incidents, suppliers, continuity and more, quoted from NIST, the NCSC, ENISA, the EDPB and CISA.
        Each step links to the page that does it, and the compliance check uses the same playbooks when it assesses you.
      </p>
      {list === null && <p style={text.hint}>Loading…</p>}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
        {(list ?? []).map((p) => (
          <ShellLink key={p.id} to={playbookPath(p.id)} style={{ ...card, display: "block", textDecoration: "none", padding: 16 }}>
            <span style={{ display: "flex", alignItems: "center", gap: 6, ...text.itemTitle, color: theme.color.heading }}>
              <BookOpenCheck size={16} style={{ color: theme.color.accentText }} /> {p.title}
            </span>
            <span style={{ ...text.hint, display: "block", marginTop: 6 }}>{p.when}</span>
            <span style={{ ...text.hint, display: "block", marginTop: 8, color: theme.color.faint }}>{p.steps} steps</span>
          </ShellLink>
        ))}
      </div>
    </div>
  );
}

function PlaybookView({ id }: { id: string }) {
  const [data, setData] = useState<{ playbook: PlaybookDetail; sources: SourceDoc[] } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setData(null); setError(null);
    api.get<{ playbook: PlaybookDetail; sources: SourceDoc[] }>(`/help/playbooks/${encodeURIComponent(id)}`)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e instanceof ApiError && e.status === 404 ? "notfound" : "This playbook could not be loaded."); });
    return () => { cancelled = true; };
  }, [id]);

  if (error) {
    return (
      <div style={{ padding: "20px 24px" }}>
        <ShellLink to="/help/playbooks" style={{ ...text.hint, ...linkStyle }}>← All playbooks</ShellLink>
        <p style={{ ...text.hint, marginTop: 8 }}>{error === "notfound" ? "Playbook not found." : error}</p>
      </div>
    );
  }
  if (!data) return <div style={{ padding: "20px 24px" }}><p style={text.hint}>Loading…</p></div>;
  const { playbook: p, sources } = data;
  const docs = new Map(sources.map((s) => [s.id, s]));

  return (
    <div style={{ padding: "20px 24px" }}>
      <ShellLink to="/help/playbooks" style={{ ...text.hint, ...linkStyle }}>← All playbooks</ShellLink>
      <h1 style={{ ...pageHeading, marginTop: 8 }}>{p.title}</h1>
      <p style={{ ...pageSubtitle, margin: "0 0 6px" }}>{p.summary}</p>
      <p style={{ ...text.hint, margin: "0 0 16px" }}><strong>When:</strong> {p.when}</p>
      {p.boundaries && (
        <div style={{ ...card, padding: "10px 14px", marginBottom: 16, display: "flex", gap: 8, alignItems: "flex-start" }}>
          <Scale size={16} style={{ color: theme.color.accentText, flexShrink: 0, marginTop: 2 }} />
          <span style={{ ...text.hint }}>{p.boundaries}</span>
        </div>
      )}

      {p.phases.map((phase, pi) => (
        <section key={pi} style={{ ...card, padding: 0, overflow: "hidden", marginBottom: 16 }}>
          <h2 style={{ ...text.sectionTitle, margin: 0, padding: "12px 16px" }}>{pi + 1}. {phase.title}</h2>
          {phase.steps.map((step, si) => (
            <div key={si} style={{ padding: "12px 16px", borderTop: "1px solid var(--c-border)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "baseline" }}>
                <span style={{ ...text.itemTitle, color: theme.color.heading }}>{step.title}</span>
                {step.to && (
                  <ShellLink to={step.to} style={{ ...text.hint, ...linkStyle, display: "inline-flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
                    Do this <ArrowRight size={12} />
                  </ShellLink>
                )}
              </div>
              {step.quotes.map((q, qi) => <QuoteBlock key={qi} quote={q} docs={docs} />)}
            </div>
          ))}
        </section>
      ))}

      {p.options.length > 0 && (
        <section style={{ ...card, padding: "12px 16px", marginBottom: 16 }}>
          <h2 style={{ ...text.sectionTitle, margin: "0 0 4px" }}>Decisions for your organisation</h2>
          <p style={{ ...text.hint, margin: "0 0 8px" }}>The sources leave these to you. This is what each one says; the platform does not choose for you.</p>
          {p.options.map((o, oi) => (
            <div key={oi} style={{ marginTop: 10 }}>
              <span style={{ ...text.itemTitle, color: theme.color.heading }}>{o.question}</span>
              {o.positions.map((q, qi) => <QuoteBlock key={qi} quote={q} docs={docs} />)}
            </div>
          ))}
        </section>
      )}

      <section style={{ ...card, padding: "12px 16px", marginBottom: 16 }}>
        <h2 style={{ ...text.sectionTitle, margin: "0 0 4px" }}>How the compliance check uses this playbook</h2>
        <p style={{ ...text.hint, margin: "0 0 8px" }}>
          The platform's own assessment criteria, not the sources' words. The check applies them to your organisation's
          size, sector, evidence and standards; your standard decides each verdict, and these shape what it looks for and recommends.
        </p>
        {p.criteria.map((c) => (
          <div key={c.id} style={{ padding: "8px 0", borderTop: "1px solid var(--c-border)" }}>
            <span style={{ ...text.itemTitle, color: theme.color.heading }}>{c.id} — {c.practice}</span>
            <span style={{ ...text.hint, display: "block", marginTop: 2 }}>Looks for: {c.evidence}</span>
            <span style={{ ...text.hint, display: "block" }}>Raised when: {c.gapWhen}</span>
          </div>
        ))}
      </section>

      <section style={{ ...card, padding: "12px 16px", marginBottom: 16 }}>
        <h2 style={{ ...text.sectionTitle, margin: "0 0 8px" }}>Sources</h2>
        {sources.map((s) => (
          <div key={s.id} style={{ padding: "6px 0", borderTop: "1px solid var(--c-border)" }}>
            {s.readable
              ? <ShellLink to={libraryPath(s.id)} style={linkStyle}>{s.title}</ShellLink>
              : <a href={s.sourceUrl} target="_blank" rel="noopener noreferrer" style={linkStyle}>{s.title} <ExternalLink size={11} /></a>}
            <span style={{ ...text.hint, display: "block" }}>{s.publisher}, {s.edition} · {s.licenceLabel}</span>
          </div>
        ))}
      </section>

      {p.related.length > 0 && (
        <p style={text.hint}>
          Related:{" "}
          {p.related.map((r, i) => (
            <span key={r.id}>{i > 0 ? " · " : ""}<ShellLink to={playbookPath(r.id)} style={linkStyle}>{r.title}</ShellLink></span>
          ))}
        </p>
      )}
    </div>
  );
}

export function PlaybooksPage() {
  const { playbookId } = useParams<{ playbookId?: string }>();
  return playbookId ? <PlaybookView id={playbookId} /> : <PlaybookList />;
}
